import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatCurrency, formatDateLong } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { FileText, Plus, Search, Pencil, Trash2, ArrowLeft, ChevronLeft, ChevronRight, Send, Check, X, Copy, ArrowRightLeft, Package, Download, Loader2, ShieldCheck, MessageSquareWarning, Mail, Tag, AlertTriangle as TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { usePriceListPricing } from "@/hooks/usePriceListPricing";

const statusColors = {
  Draft: "bg-gray-100 text-gray-700 border-gray-300",
  "Pending Approval": "bg-amber-50 text-amber-700 border-amber-200",
  Sent: "bg-blue-50 text-blue-700 border-blue-200",
  Accepted: "bg-green-50 text-green-700 border-green-200",
  Rejected: "bg-red-50 text-red-700 border-red-200",
  Expired: "bg-yellow-50 text-yellow-700 border-yellow-200",
  Converted: "bg-purple-50 text-purple-700 border-purple-200",
};

function triggerDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

export default function QuotesPage() {
  const { id } = useParams();
  if (id) return <QuoteDetail quoteId={id} />;
  return <QuoteList />;
}

function QuoteList() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const canCreate = ["admin", "sales_manager", "sales_rep"].includes(user?.role);
  const canDelete = ["admin", "sales_manager"].includes(user?.role);

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/quotes", { params: { search, status: statusFilter, page, limit: 25 } });
      setQuotes(res.data.quotes);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
      setStats(res.data.stats || {});
      setSelected([]);
    } catch { toast.error("Failed to load quotes"); }
    finally { setLoading(false); }
  }, [search, statusFilter, page]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  const handleDelete = async () => {
    try { await api.delete(`/quotes/${deleteId}`); toast.success("Quote deleted"); setDeleteId(null); fetchQuotes(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const toggleSelect = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleAll = () => setSelected(selected.length === quotes.length ? [] : quotes.map((q) => q.quote_id));

  const handleBulkDownload = async (ids) => {
    if (ids.length === 0) return;
    setDownloading(true);
    try {
      if (ids.length === 1) {
        const res = await api.get(`/quotes/${ids[0]}/pdf`, { responseType: "blob" });
        const q = quotes.find((x) => x.quote_id === ids[0]);
        triggerDownload(new Blob([res.data]), `Quote_${q?.quote_number || "QT"}.pdf`);
        toast.success("Quote PDF downloaded successfully");
      } else {
        const res = await api.post("/quotes/bulk-pdf", { quote_ids: ids }, { responseType: "blob" });
        triggerDownload(new Blob([res.data]), `Supply_House_Quotes_${new Date().toISOString().split("T")[0]}.zip`);
        toast.success(`${ids.length} quote PDFs downloaded successfully`);
      }
      setSelected([]);
    } catch { toast.error("Failed to download PDFs"); }
    finally { setDownloading(false); }
  };

  return (
    <div className="animate-fade-in" data-testid="quotes-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#1F2937] tracking-tight">Quotes</h1>
          <div className="flex flex-wrap items-center gap-3 mt-1">
            <Badge className="bg-gray-100 text-gray-600 rounded-full text-xs">{stats.draft || 0} Draft</Badge>
            {(stats.pending_approval || 0) > 0 && <Badge className="bg-amber-50 text-amber-700 rounded-full text-xs">{stats.pending_approval} Pending</Badge>}
            <Badge className="bg-blue-50 text-blue-700 rounded-full text-xs">{stats.sent || 0} Sent</Badge>
            <Badge className="bg-green-50 text-green-700 rounded-full text-xs">{stats.accepted || 0} Accepted</Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selected.length > 0 && (
            <Button data-testid="bulk-download-quotes" onClick={() => handleBulkDownload(selected)} disabled={downloading} variant="outline" className="rounded-sm border-gray-300">
              {downloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
              {downloading ? "Generating..." : `Download ${selected.length} PDF${selected.length > 1 ? "s" : ""}`}
            </Button>
          )}
          {(search || statusFilter) && total > 0 && selected.length === 0 && (
            <Button data-testid="download-filtered-quotes" variant="outline" disabled={downloading}
              onClick={() => handleBulkDownload(quotes.map((q) => q.quote_id))} className="rounded-sm border-gray-300">
              {downloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
              Download {quotes.length} Filtered
            </Button>
          )}
          {canCreate && (
            <Button data-testid="create-quote-button" onClick={() => setDialogOpen(true)} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
              <Plus className="w-4 h-4 mr-1.5" /> New Quote
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="quote-search-input" placeholder="Search by quote # or customer..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9 rounded-sm border-gray-300" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="quote-status-filter" className="w-[150px] h-9 rounded-sm border-gray-300"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {["Draft", "Pending Approval", "Sent", "Accepted", "Rejected", "Expired", "Converted"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-gray-200 rounded-sm shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-3 w-10"><Checkbox data-testid="select-all-quotes" checked={quotes.length > 0 && selected.length === quotes.length} onCheckedChange={toggleAll} /></th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Quote #</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Valid Until</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => <tr key={i} className="border-b border-gray-100"><td colSpan={8} className="px-4 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td></tr>)
              ) : quotes.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-400">
                  <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No quotes found</p>
                  <p className="text-xs mt-1">Create your first quote to get started</p>
                </td></tr>
              ) : quotes.map((q) => (
                <tr key={q.quote_id} data-testid={`quote-row-${q.quote_id}`}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer">
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.includes(q.quote_id)} onCheckedChange={() => toggleSelect(q.quote_id)} />
                  </td>
                  <td className="px-4 py-3 font-mono text-sm font-medium text-[#E63946]" onClick={() => navigate(`/quotes/${q.quote_id}`)}>{q.quote_number}</td>
                  <td className="px-4 py-3 font-medium text-[#1F2937] max-w-[200px] truncate" onClick={() => navigate(`/quotes/${q.quote_id}`)} title={q.customer_name}>{q.customer_name}</td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell" onClick={() => navigate(`/quotes/${q.quote_id}`)}>{formatDateLong(q.created_at)}</td>
                  <td className="px-4 py-3 text-gray-600 hidden lg:table-cell" onClick={() => navigate(`/quotes/${q.quote_id}`)}>{q.valid_until || "-"}</td>
                  <td className="px-4 py-3 text-right font-medium text-[#1F2937]" onClick={() => navigate(`/quotes/${q.quote_id}`)}>{formatCurrency(q.total)}</td>
                  <td className="px-4 py-3" onClick={() => navigate(`/quotes/${q.quote_id}`)}><Badge className={`rounded-full text-xs ${statusColors[q.status] || ""}`}>{q.status}</Badge></td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-blue-600" title="Download PDF"
                        onClick={() => handleBulkDownload([q.quote_id])}><Download className="w-4 h-4" /></Button>
                      {canDelete && ["Draft", "Pending Approval"].includes(q.status) && (
                        <Button data-testid={`delete-quote-${q.quote_id}`} variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-[#E63946]" onClick={() => setDeleteId(q.quote_id)}><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="h-8 rounded-sm"><ChevronLeft className="w-4 h-4" /></Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="h-8 rounded-sm"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </Card>

      <QuoteFormDialog open={dialogOpen} onOpenChange={setDialogOpen} onSuccess={() => { setDialogOpen(false); fetchQuotes(); }} />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Quote?</AlertDialogTitle><AlertDialogDescription>This will permanently delete this quote.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-quote" onClick={handleDelete} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function QuoteFormDialog({ open, onOpenChange, onSuccess, editData }) {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerId, setCustomerId] = useState(editData?.customer_id || "");
  const [items, setItems] = useState(editData?.line_items || []);
  const [discountType, setDiscountType] = useState(editData?.discount_type || "percentage");
  const [discountValue, setDiscountValue] = useState(editData?.discount_value || 0);
  const [vatRate, setVatRate] = useState(editData?.vat_rate || 15);
  const [validUntil, setValidUntil] = useState(editData?.valid_until || "");
  const [reference, setReference] = useState(editData?.reference || "");
  const [notes, setNotes] = useState(editData?.notes || "");
  const [terms, setTerms] = useState(editData?.terms || "");
  const [saving, setSaving] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [overrideModalIdx, setOverrideModalIdx] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");

  const { priceListInfo, loadingPrices, fetchPrice, onQuantityChange, onPriceChange, hasUnresolvedOverrides, overrideCount } = usePriceListPricing(customerId, items, setItems);

  useEffect(() => {
    if (open) {
      api.get("/customers", { params: { limit: 200, status: "Active" } }).then((r) => setCustomers(r.data.customers || [])).catch(() => {});
      api.get("/products", { params: { limit: 200, status: "Active" } }).then((r) => setProducts(r.data.products || [])).catch(() => {});
      api.get("/settings").then((r) => {
        if (!editData) {
          setTerms(r.data.default_quote_terms || "");
          setVatRate(r.data.default_vat_rate || 15);
        }
      }).catch(() => {});
      if (!editData) {
        setCustomerId(""); setItems([]); setDiscountType("percentage"); setDiscountValue(0); setReference(""); setNotes("");
        const d = new Date(); d.setDate(d.getDate() + 30);
        setValidUntil(d.toISOString().split("T")[0]);
      }
    }
  }, [open, editData]);

  const addProduct = async (product) => {
    let unitPrice = product.selling_price || 0;
    let priceSource = "";
    let listPrice = 0;
    if (customerId && product.product_id) {
      const priceData = await fetchPrice(product.product_id, 1);
      if (priceData && priceData.source !== "base") {
        unitPrice = priceData.price;
        priceSource = priceData.price_list || "";
        listPrice = priceData.price;
      }
    }
    setItems([...items, { item_id: `item_${Date.now()}`, product_id: product.product_id, sku: product.sku, description: product.product_name, quantity: 1, unit_price: unitPrice, discount_percent: 0, price_source: priceSource, list_price: listPrice, is_override: false, override_reason: "" }]);
    setProductSearch("");
  };
  const addCustomItem = () => setItems([...items, { item_id: `item_${Date.now()}`, product_id: "", sku: "", description: "", quantity: 1, unit_price: 0, discount_percent: 0, price_source: "", list_price: 0, is_override: false, override_reason: "" }]);
  const updateItem = (idx, field, value) => { const next = [...items]; next[idx] = { ...next[idx], [field]: value }; setItems(next); };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const calcLineTotal = (item) => {
    const sub = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
    return sub - (sub * (parseFloat(item.discount_percent) || 0) / 100);
  };
  const subtotal = items.reduce((sum, i) => sum + calcLineTotal(i), 0);
  const discAmt = discountType === "percentage" ? subtotal * (parseFloat(discountValue) || 0) / 100 : parseFloat(discountValue) || 0;
  const afterDisc = subtotal - discAmt;
  const vatAmt = afterDisc * (parseFloat(vatRate) || 0) / 100;
  const total = afterDisc + vatAmt;

  const filteredProducts = productSearch ? products.filter((p) =>
    p.product_name?.toLowerCase().includes(productSearch.toLowerCase()) || p.sku?.toLowerCase().includes(productSearch.toLowerCase())
  ).slice(0, 8) : [];

  const handleSave = async () => {
    if (!customerId) { toast.error("Please select a customer"); return; }
    if (items.length === 0) { toast.error("Add at least one line item"); return; }
    if (items.some((i) => !i.description?.trim())) { toast.error("All items must have a description"); return; }
    if (hasUnresolvedOverrides) { toast.error("Please provide a reason for all price overrides"); return; }
    setSaving(true);
    try {
      const payload = {
        customer_id: customerId, line_items: items, discount_type: discountType, discount_value: discountValue,
        vat_rate: vatRate, valid_until: validUntil, reference, notes, terms,
        price_list_id: priceListInfo?.price_list_id || "", price_list_name: priceListInfo?.price_list_name || "",
      };
      if (editData) { await api.put(`/quotes/${editData.quote_id}`, payload); toast.success("Quote updated"); }
      else { await api.post("/quotes", payload); toast.success("Quote created"); }
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save quote"); }
    finally { setSaving(false); }
  };

  const openOverrideModal = (idx) => { setOverrideModalIdx(idx); setOverrideReason(items[idx]?.override_reason || ""); };
  const confirmOverride = () => {
    if (overrideModalIdx !== null) {
      updateItem(overrideModalIdx, "override_reason", overrideReason);
      setOverrideModalIdx(null);
      setOverrideReason("");
    }
  };
  const cancelOverride = () => {
    if (overrideModalIdx !== null) {
      const item = items[overrideModalIdx];
      if (item.list_price) {
        const next = [...items];
        next[overrideModalIdx] = { ...item, unit_price: item.list_price, is_override: false, override_reason: "" };
        setItems(next);
      }
    }
    setOverrideModalIdx(null); setOverrideReason("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading font-semibold text-xl">{editData ? "Edit Quote" : "Create New Quote"}</DialogTitle></DialogHeader>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Customer <span className="text-[#E63946]">*</span></label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger data-testid="quote-customer-select" className="rounded-sm border-gray-300 h-10"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Valid Until</label>
              <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="rounded-sm border-gray-300 h-10" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Reference</label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional reference" className="rounded-sm border-gray-300 h-10" />
            </div>
          </div>

          {/* Price List Indicator Banner */}
          {priceListInfo && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-sm px-3 py-2" data-testid="price-list-banner">
              <Tag className="w-4 h-4 text-blue-600 shrink-0" />
              <p className="text-sm text-blue-800 flex-1">
                Using Price List: <span className="font-semibold">{priceListInfo.price_list_name}</span>
                {priceListInfo.is_system_default && <span className="text-blue-500 ml-1">(System Default)</span>}
                <span className="text-blue-400 ml-2">({priceListInfo.type} &middot; {priceListInfo.product_count} products)</span>
              </p>
              {loadingPrices && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
            </div>
          )}

          {/* Override Summary Warning */}
          {overrideCount > 0 && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid="override-warning-banner">
              <TriangleAlert className="w-4 h-4 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-800">
                {overrideCount} price {overrideCount === 1 ? "override" : "overrides"} detected.
                {hasUnresolvedOverrides && <span className="font-semibold text-amber-900 ml-1">Reasons required before saving.</span>}
              </p>
            </div>
          )}

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-[#374151]">Line Items <span className="text-[#E63946]">*</span></label>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Input data-testid="product-search-add" value={productSearch} onChange={(e) => setProductSearch(e.target.value)}
                    placeholder="Search products..." className="rounded-sm border-gray-300 h-8 w-48 text-xs" />
                  {filteredProducts.length > 0 && (
                    <div className="absolute z-50 top-full left-0 w-72 bg-white border border-gray-200 rounded-sm shadow-lg mt-1 max-h-48 overflow-y-auto">
                      {filteredProducts.map((p) => (
                        <button key={p.product_id} onClick={() => addProduct(p)}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between border-b border-gray-100 last:border-0">
                          <span className="truncate">{p.product_name}</span>
                          <span className="text-xs text-gray-400 ml-2 shrink-0">{p.sku} - {formatCurrency(p.selling_price)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addCustomItem} className="rounded-sm text-xs h-8"><Plus className="w-3 h-3 mr-1" /> Custom Item</Button>
              </div>
            </div>
            {items.length === 0 ? (
              <div className="border-2 border-dashed border-gray-200 rounded-sm p-8 text-center">
                <Package className="w-8 h-8 mx-auto mb-2 text-gray-300" /><p className="text-sm text-gray-400">Search for products above or add a custom item</p>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Description</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-20">Qty</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-32">Unit Price</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-20">Disc%</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-28">Total</th>
                    <th className="w-10"></th>
                  </tr></thead>
                  <tbody>
                    {items.map((item, idx) => {
                      const listP = parseFloat(item.list_price) || 0;
                      const curP = parseFloat(item.unit_price) || 0;
                      const diff = listP > 0 ? curP - listP : 0;
                      const pctDiff = listP > 0 ? ((diff / listP) * 100).toFixed(1) : 0;
                      return (
                        <tr key={item.item_id} className={`border-b border-gray-100 ${item.is_override ? "bg-amber-50/50" : ""}`}>
                          <td className="px-2 py-1.5"><Input value={item.description} onChange={(e) => updateItem(idx, "description", e.target.value)} placeholder="Item description" className="rounded-sm border-gray-200 h-8 text-sm" /></td>
                          <td className="px-2 py-1.5"><Input type="number" value={item.quantity}
                            onChange={(e) => item.product_id ? onQuantityChange(idx, e.target.value) : updateItem(idx, "quantity", e.target.value)}
                            className="rounded-sm border-gray-200 h-8 text-sm text-right" /></td>
                          <td className="px-2 py-1.5">
                            <Input type="number" step="0.01" value={item.unit_price}
                              onChange={(e) => item.list_price > 0 ? onPriceChange(idx, e.target.value) : updateItem(idx, "unit_price", e.target.value)}
                              className={`rounded-sm h-8 text-sm text-right ${item.is_override ? "border-amber-400 bg-amber-50" : "border-gray-200"}`} />
                            {item.price_source && !item.is_override && (
                              <p className="text-[10px] text-blue-500 text-right mt-0.5" data-testid={`price-source-${idx}`}>{item.price_source}</p>
                            )}
                            {item.is_override && (
                              <div className="text-right mt-0.5">
                                <span className={`text-[10px] font-semibold ${diff > 0 ? "text-red-500" : "text-green-600"}`} data-testid={`override-indicator-${idx}`}>
                                  {diff > 0 ? "+" : ""}{formatCurrency(diff)} ({diff > 0 ? "+" : ""}{pctDiff}%)
                                </span>
                                <button onClick={() => openOverrideModal(idx)} className="text-[10px] text-amber-600 hover:text-amber-800 ml-1.5 underline" data-testid={`override-reason-btn-${idx}`}>
                                  {item.override_reason ? "Edit reason" : "Add reason"}
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-1.5"><Input type="number" step="0.1" value={item.discount_percent} onChange={(e) => updateItem(idx, "discount_percent", e.target.value)} className="rounded-sm border-gray-200 h-8 text-sm text-right" /></td>
                          <td className="px-3 py-1.5 text-right font-medium text-[#1F2937]">{formatCurrency(calcLineTotal(item))}</td>
                          <td className="px-1 py-1.5"><Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-[#E63946]" onClick={() => removeItem(idx)}><X className="w-3.5 h-3.5" /></Button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {items.length > 0 && (
            <div className="flex justify-end">
              <div className="w-72 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-500">Subtotal</span><span>{formatCurrency(subtotal)}</span></div>
                <div className="flex items-center justify-between text-sm gap-2">
                  <span className="text-gray-500">Discount</span>
                  <div className="flex items-center gap-1">
                    <Select value={discountType} onValueChange={setDiscountType}><SelectTrigger className="h-7 w-16 rounded-sm border-gray-200 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="percentage">%</SelectItem><SelectItem value="fixed">R</SelectItem></SelectContent></Select>
                    <Input type="number" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} className="h-7 w-16 rounded-sm border-gray-200 text-xs text-right" />
                  </div>
                  <span className="text-[#E63946]">-{formatCurrency(discAmt)}</span>
                </div>
                <div className="flex items-center justify-between text-sm gap-2">
                  <span className="text-gray-500">VAT</span>
                  <Input type="number" value={vatRate} onChange={(e) => setVatRate(e.target.value)} className="h-7 w-16 rounded-sm border-gray-200 text-xs text-right" />
                  <span>{formatCurrency(vatAmt)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t border-gray-200 pt-2"><span>Total</span><span className="text-[#E63946]">{formatCurrency(total)}</span></div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-[#374151] mb-1">Notes</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes for the customer..."
                className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-20 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:ring-offset-1" /></div>
            <div><label className="block text-sm font-medium text-[#374151] mb-1">Terms & Conditions</label>
              <textarea value={terms} onChange={(e) => setTerms(e.target.value)} placeholder="Terms & conditions..."
                className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-20 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:ring-offset-1" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button data-testid="save-quote-button" onClick={handleSave} disabled={saving || hasUnresolvedOverrides} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
            {saving ? "Saving..." : editData ? "Update Quote" : "Create Quote"}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Override Reason Modal */}
      <Dialog open={overrideModalIdx !== null} onOpenChange={(o) => { if (!o) cancelOverride(); }}>
        <DialogContent className="max-w-md" data-testid="override-reason-modal">
          <DialogHeader><DialogTitle className="text-lg font-semibold">Price Override Reason</DialogTitle></DialogHeader>
          {overrideModalIdx !== null && items[overrideModalIdx] && (() => {
            const item = items[overrideModalIdx];
            const lp = parseFloat(item.list_price) || 0;
            const cp = parseFloat(item.unit_price) || 0;
            const d = cp - lp;
            return (
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-sm p-3 space-y-1.5 text-sm">
                  <p><span className="text-gray-500">Product:</span> <span className="font-medium">{item.description}</span></p>
                  <div className="grid grid-cols-2 gap-2">
                    <p><span className="text-gray-500">List Price:</span> <span className="font-medium">{formatCurrency(lp)}</span></p>
                    <p><span className="text-gray-500">Override Price:</span> <span className={`font-bold ${d > 0 ? "text-red-600" : "text-green-600"}`}>{formatCurrency(cp)}</span></p>
                  </div>
                  <p><span className="text-gray-500">Difference:</span> <span className={`font-semibold ${d > 0 ? "text-red-600" : "text-green-600"}`}>{d > 0 ? "+" : ""}{formatCurrency(d)} ({lp > 0 ? ((d / lp) * 100).toFixed(1) : 0}%)</span></p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1">Reason for Override <span className="text-[#E63946]">*</span></label>
                  <textarea value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="e.g., Bulk deal negotiation, Special promotion, Customer loyalty discount..."
                    className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-20 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:ring-offset-1" data-testid="override-reason-input" />
                  <p className="text-xs text-gray-400 mt-1">This will be tracked for audit purposes</p>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={cancelOverride} className="rounded-sm">Revert to List Price</Button>
            <Button onClick={confirmOverride} disabled={!overrideReason.trim()} className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm" data-testid="confirm-override-btn">Confirm Override</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function QuoteDetail({ quoteId }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [changesOpen, setChangesOpen] = useState(false);
  const [changesNotes, setChangesNotes] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = ["admin", "sales_manager"].includes(user?.role);
  const isOwner = quote?.created_by === user?.user_id;
  const canEdit = isManager || (user?.role === "sales_rep" && isOwner && ["Draft", "Pending Approval"].includes(quote?.status));

  const fetchQuote = useCallback(async () => {
    try { const res = await api.get(`/quotes/${quoteId}`); setQuote(res.data); }
    catch { toast.error("Quote not found"); }
    finally { setLoading(false); }
  }, [quoteId]);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  const handleAction = async (action, body = {}) => {
    try {
      await api.post(`/quotes/${quoteId}/${action}`, body);
      toast.success(`Quote ${action === "send" ? "sent" : action === "accept" ? "accepted" : action === "reject" ? "rejected" : action === "duplicate" ? "duplicated" : "converted"} successfully`);
      if (action === "convert") navigate("/invoices");
      else if (action === "duplicate") navigate("/quotes");
      else fetchQuote();
    } catch (err) { toast.error(err.response?.data?.detail || `Failed to ${action} quote`); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/quotes/${quoteId}`); toast.success("Quote deleted"); navigate("/quotes"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get(`/quotes/${quoteId}/pdf`, { responseType: "blob" });
      triggerDownload(new Blob([res.data]), `Quote_${quote.quote_number}.pdf`);
      toast.success("Quote PDF downloaded");
    } catch { toast.error("Failed to generate PDF"); }
    finally { setPdfLoading(false); }
  };

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-sm" />;
  if (!quote) return <p className="text-gray-400">Quote not found</p>;

  return (
    <div className="animate-fade-in" data-testid="quote-detail">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/quotes")} className="h-8 w-8"><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <h1 className="font-heading font-bold text-2xl text-[#1F2937]">{quote.quote_number}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={`rounded-full text-xs ${statusColors[quote.status] || ""}`}>{quote.status}</Badge>
            {quote.email_sent && <Badge className="rounded-full text-xs bg-green-50 text-green-700 border border-green-200"><Mail className="w-3 h-3 mr-1 inline" />Emailed</Badge>}
            <span className="text-sm text-gray-500">{quote.customer_name}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="download-quote-pdf" variant="outline" size="sm" onClick={handleDownloadPdf} disabled={pdfLoading} className="rounded-sm">
            {pdfLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} {pdfLoading ? "Generating..." : "Download PDF"}
          </Button>
          {quote.status === "Pending Approval" && isManager && (
            <>
              <Button data-testid="approve-quote-btn" size="sm" onClick={() => handleAction("approve")} className="bg-green-600 hover:bg-green-700 text-white rounded-sm"><ShieldCheck className="w-3.5 h-3.5 mr-1" /> Approve</Button>
              <Button data-testid="request-changes-btn" variant="outline" size="sm" onClick={() => setChangesOpen(true)} className="rounded-sm text-amber-600 border-amber-200 hover:bg-amber-50"><MessageSquareWarning className="w-3.5 h-3.5 mr-1" /> Request Changes</Button>
            </>
          )}
          {["Draft", "Pending Approval"].includes(quote.status) && canEdit && (
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} className="rounded-sm"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
          )}
          {quote.status === "Draft" && isManager && (
            <Button size="sm" onClick={() => handleAction("send")} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm"><Send className="w-3.5 h-3.5 mr-1" /> Send</Button>
          )}
          {quote.email_sent && quote.status !== "Draft" && isManager && (
            <Button variant="outline" size="sm" onClick={async () => { try { await api.post("/email/resend", { type: "quote", id: quote.quote_id }); toast.success("Email resent successfully"); } catch { toast.error("Failed to resend email"); }}} className="rounded-sm text-blue-600 border-blue-200 hover:bg-blue-50"><Mail className="w-3.5 h-3.5 mr-1" /> Resend Email</Button>
          )}
          {quote.status === "Sent" && isManager && (
            <>
              <Button size="sm" onClick={() => handleAction("accept")} className="bg-green-600 hover:bg-green-700 text-white rounded-sm"><Check className="w-3.5 h-3.5 mr-1" /> Accept</Button>
              <Button variant="outline" size="sm" onClick={() => setRejectOpen(true)} className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
            </>
          )}
          {["Accepted", "Sent"].includes(quote.status) && !quote.converted_to_invoice && isManager && (
            <Button size="sm" onClick={() => handleAction("convert")} className="bg-purple-600 hover:bg-purple-700 text-white rounded-sm"><ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Convert to Invoice</Button>
          )}
          <Button variant="outline" size="sm" onClick={() => handleAction("duplicate")} className="rounded-sm"><Copy className="w-3.5 h-3.5 mr-1" /> Duplicate</Button>
          {["Draft", "Pending Approval"].includes(quote.status) && isManager && (
            <Button variant="outline" size="sm" onClick={() => setDeleteOpen(true)} className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {quote.status === "Pending Approval" && (
            <Card className="border-2 border-amber-300 bg-amber-50 rounded-sm shadow-sm">
              <CardContent className="p-4 flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Awaiting Manager Approval</p>
                  <p className="text-xs text-amber-700 mt-0.5">This quote was created by {quote.created_by_name} and requires approval from a Sales Manager or Admin before it can be sent.</p>
                  {quote.change_request_notes && (
                    <div className="mt-2 p-2 bg-amber-100 rounded-sm">
                      <p className="text-xs font-medium text-amber-800">Changes Requested:</p>
                      <p className="text-xs text-amber-700 mt-0.5">{quote.change_request_notes}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          <Card className="border border-gray-200 rounded-sm shadow-sm">
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Customer</p><p className="text-sm font-medium">{quote.customer_name}</p></div>
                <div><p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Created</p><p className="text-sm">{formatDateLong(quote.created_at)}</p></div>
                <div><p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Valid Until</p><p className="text-sm">{quote.valid_until || "-"}</p></div>
                <div><p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Reference</p><p className="text-sm">{quote.reference || "-"}</p></div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-gray-200 rounded-sm shadow-sm">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Description</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Qty</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Unit Price</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Disc%</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Total</th>
                </tr></thead>
                <tbody>
                  {(quote.line_items || []).map((item) => (
                    <tr key={item.item_id} className="border-b border-gray-100">
                      <td className="px-4 py-3"><span className="font-medium">{item.description}</span>{item.sku && <span className="text-xs text-gray-400 ml-2">({item.sku})</span>}</td>
                      <td className="px-4 py-3 text-right">{item.quantity}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(item.unit_price)}</td>
                      <td className="px-4 py-3 text-right">{item.discount_percent > 0 ? `${item.discount_percent}%` : "-"}</td>
                      <td className="px-4 py-3 text-right font-medium">{formatCurrency(item.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-4 border-t border-gray-200 flex justify-end">
                <div className="w-64 space-y-1.5">
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Subtotal</span><span>{formatCurrency(quote.subtotal)}</span></div>
                  {quote.discount_amount > 0 && <div className="flex justify-between text-sm"><span className="text-gray-500">Discount</span><span className="text-[#E63946]">-{formatCurrency(quote.discount_amount)}</span></div>}
                  <div className="flex justify-between text-sm"><span className="text-gray-500">VAT ({quote.vat_rate}%)</span><span>{formatCurrency(quote.vat_amount)}</span></div>
                  <div className="flex justify-between font-bold text-lg border-t border-gray-200 pt-2"><span>Total</span><span className="text-[#E63946]">{formatCurrency(quote.total)}</span></div>
                </div>
              </div>
            </CardContent>
          </Card>

          {(quote.notes || quote.terms) && (
            <Card className="border border-gray-200 rounded-sm shadow-sm">
              <CardContent className="p-6">
                {quote.notes && <div className="mb-4"><p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Notes</p><p className="text-sm whitespace-pre-wrap">{quote.notes}</p></div>}
                {quote.terms && <div><p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Terms & Conditions</p><p className="text-sm whitespace-pre-wrap">{quote.terms}</p></div>}
              </CardContent>
            </Card>
          )}
        </div>

        <div>
          <Card className="border border-gray-200 rounded-sm shadow-sm">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-[#1F2937] mb-3">Activity</h3>
              <div className="space-y-3">
                {(quote.audit_trail || []).map((entry, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#E63946] mt-1.5 shrink-0" />
                    <div><p className="text-xs text-gray-700">{entry.details}</p><p className="text-xs text-gray-400">{entry.user_name} - {formatDateLong(entry.created_at)}</p></div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <QuoteFormDialog open={editOpen} onOpenChange={setEditOpen} editData={quote} onSuccess={() => { setEditOpen(false); fetchQuote(); }} />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Quote?</AlertDialogTitle><AlertDialogDescription>This will permanently delete {quote.quote_number}.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Reject Quote?</AlertDialogTitle>
            <AlertDialogDescription>
              <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason for rejection (optional)..."
                className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-20 resize-none mt-2 focus:outline-none focus:ring-2 focus:ring-[#E63946]" />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { handleAction("reject", { reason: rejectReason }); setRejectOpen(false); }} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Reject Quote</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={changesOpen} onOpenChange={setChangesOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Request Changes</AlertDialogTitle>
            <AlertDialogDescription>
              <p className="text-sm text-gray-500 mb-2">Explain what changes are needed. The sales rep will be able to edit and resubmit.</p>
              <textarea data-testid="changes-notes-input" value={changesNotes} onChange={(e) => setChangesNotes(e.target.value)} placeholder="Describe the changes needed..."
                className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-24 resize-none focus:outline-none focus:ring-2 focus:ring-amber-500" />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-request-changes" onClick={() => { handleAction("request-changes", { notes: changesNotes }); setChangesOpen(false); setChangesNotes(""); }} className="bg-amber-500 hover:bg-amber-600 text-white rounded-sm">Send Request</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
